import { useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc, getDocFromCache } from "firebase/firestore";
import type { DocumentData, DocumentSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { normalizeEntityType } from "../types";
import type { EntityType, ActivityOption, CustomQuestion } from "../types";
import { getCustomQuestions } from "../utils/customQuestions";

export interface EntityInfo {
  displayName: string;
  imageUrl: string | null;
  type: EntityType | null;
  /** The entity's selectable options (access tiers / service types). Lets
   *  analytics resolve an interaction's accessTierId to a readable name. */
  activityOptions: ActivityOption[];
  /** The place's own questions, so a list can show "Song: Conga" by label. */
  customQuestions: CustomQuestion[];
}

/** Every entity read this session, so a page coming back draws names and
 *  types on its first frame rather than after the cache read. */
const seenInfo = new Map<string, EntityInfo>();

/** Forget the session's entities (tests). */
export function clearSeenEntities(): void {
  seenInfo.clear();
}

/** How often to re-try ids whose reads failed (offline and not cached). */
const RETRY_INTERVAL_MS = 30_000;

function toEntityInfo(data: DocumentData): EntityInfo {
  return {
    displayName: data.displayName ?? "",
    imageUrl: data.imageUrl ?? null,
    type: normalizeEntityType(data.type),
    activityOptions: (data.activityOptions as ActivityOption[]) ?? [],
    customQuestions: getCustomQuestions({
      customConfig: (data.customConfig as Record<string, unknown>) ?? {},
    }),
  };
}

/** A read that failed resolves to null rather than rejecting. */
const orNull = (p: Promise<DocumentSnapshot>) => p.catch(() => null);

/**
 * Given an array of entity IDs, returns a map of entityId → EntityInfo.
 * Fetches each entity doc once and caches within the hook lifecycle.
 *
 * Offline behaviour is the point of most of the shape here:
 *   - Each id is read from the local cache first, so anything this device has
 *     seen renders instantly with no network. A server read follows to pick up
 *     edits made elsewhere (with Firestore's network disabled it just answers
 *     from cache again, quickly).
 *   - `loading` is about *attempts*, not results. An entity that's deleted, or
 *     that this device never cached and can't reach, will never appear in the
 *     map — keying loading on "every id is in the map" left detail pages on
 *     "Loading..." for the whole cruise.
 *   - `loading` is only ever true for the first fetch. Ids that show up later
 *     (a new activity logged) fill in as they arrive rather than blanking a
 *     list that's already on screen.
 *   - Failed reads are retried on the browser's `online` event and on a slow
 *     timer, so names appear once connectivity returns without a remount.
 */
export function useEntityMap(entityIds: string[]) {
  const [entityMap, setEntityMap] = useState<Record<string, string>>({});
  const [entityDataMap, setEntityDataMap] = useState<Record<string, EntityInfo>>({});
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [hasFailures, setHasFailures] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  // Per-id bookkeeping lives in refs: it steers which ids a run fetches but
  // never needs to trigger a render by itself.
  /** Ids we have an answer for (found, or confirmed missing). Never refetched. */
  const settledRef = useRef<Set<string>>(new Set());
  /** Ids with a read in flight, so overlapping runs don't double-fetch. */
  const inFlightRef = useRef<Set<string>>(new Set());
  /** Ids whose last attempt got no answer at all; retried later. */
  const failedRef = useRef<Set<string>>(new Set());
  const aliveRef = useRef(true);

  // Stable key for the effect dependency
  const idsKey = entityIds.slice().sort().join(",");

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    const pending = [...new Set(entityIds)].filter(
      (id) => !settledRef.current.has(id) && !inFlightRef.current.has(id),
    );
    if (pending.length === 0) return;
    pending.forEach((id) => inFlightRef.current.add(id));

    const merge = (found: Record<string, DocumentData>, missing: string[]) => {
      if (!aliveRef.current) return;
      setEntityMap((prev) => {
        const next = { ...prev };
        for (const [id, data] of Object.entries(found)) next[id] = data.displayName ?? null;
        for (const id of missing) delete next[id];
        return next;
      });
      setEntityDataMap((prev) => {
        const next = { ...prev };
        for (const [id, data] of Object.entries(found)) {
          next[id] = toEntityInfo(data);
          seenInfo.set(id, next[id]);
        }
        for (const id of missing) {
          delete next[id];
          seenInfo.delete(id);
        }
        return next;
      });
    };

    async function fetchEntities(ids: string[]) {
      const refs = ids.map((id) => doc(db, "entities", id));

      // Phase 1: local cache. Rejects for docs this device has never seen.
      const cached = await Promise.all(refs.map((r) => orNull(getDocFromCache(r))));
      const cacheFound: Record<string, DocumentData> = {};
      ids.forEach((id, i) => {
        const snap = cached[i];
        if (snap?.exists()) cacheFound[id] = snap.data();
      });
      merge(cacheFound, []);
      if (aliveRef.current && cached.every((snap) => snap !== null)) {
        setInitialFetchDone(true);
      }

      // Phase 2: server (or cache again while Firestore's network is off).
      const fresh = await Promise.all(refs.map((r) => orNull(getDoc(r))));
      const found: Record<string, DocumentData> = {};
      const missing: string[] = [];
      ids.forEach((id, i) => {
        const snap = fresh[i];
        inFlightRef.current.delete(id);
        if (snap) {
          if (snap.exists()) found[id] = snap.data();
          else missing.push(id);
          settledRef.current.add(id);
          failedRef.current.delete(id);
        } else if (cached[i]) {
          // The server read failed but the cache answered: good enough.
          settledRef.current.add(id);
          failedRef.current.delete(id);
        } else {
          failedRef.current.add(id);
        }
      });
      merge(found, missing);
      if (aliveRef.current) {
        setInitialFetchDone(true);
        setHasFailures(failedRef.current.size > 0);
      }
    }

    void fetchEntities(pending);
    // Same reason as elsewhere: `entityIds` is rebuilt every render, so the
    // sorted-and-joined `idsKey` is what actually identifies the request.
    // `retryTick` re-runs the same filter, which picks the failed ids back up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, retryTick]);

  // Retry failed reads when connectivity may have come back. `online` covers
  // the browser noticing; the timer covers ship wifi, where the browser never
  // thought it was offline and only the app's reachability probe knows. A
  // failed retry is a fast cache miss, so the timer is cheap.
  useEffect(() => {
    if (!hasFailures) return;
    const retry = () => setRetryTick((n) => n + 1);
    window.addEventListener("online", retry);
    const intervalId = window.setInterval(retry, RETRY_INTERVAL_MS);
    return () => {
      window.removeEventListener("online", retry);
      window.clearInterval(intervalId);
    };
  }, [hasFailures]);

  const loading = entityIds.length > 0 && !initialFetchDone;

  // Fill in anything read earlier this session that this mount hasn't yet.
  const filled = useMemo(() => {
    const extra = entityIds.filter((id) => !(id in entityDataMap) && seenInfo.has(id));
    if (extra.length === 0) return { entityMap, entityDataMap };
    const names = { ...entityMap };
    const data = { ...entityDataMap };
    for (const id of extra) {
      const info = seenInfo.get(id)!;
      data[id] = info;
      names[id] = info.displayName;
    }
    return { entityMap: names, entityDataMap: data };
    // idsKey identifies entityIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, entityMap, entityDataMap]);

  return { entityMap: filled.entityMap, entityDataMap: filled.entityDataMap, loading };
}
